import React, { useState } from "react";
import styled, { css } from "styled-components";
import { RouterProps } from "react-router";
import { Theme } from "../../theme";
import { useTheme } from "@material-ui/styles";
import { Grid, Typography, SnackbarContent, Button } from "@material-ui/core";

import SigninForm from "./Views/SigninForm";
import SignupForm from "./Views/SignupForm";
import Signout from "./Views/Signout";
import VerifyForm from "./Views/VerifyForm";
import ForgotPassword from "./Views/ForgotPassword";
import authService, { STAGE } from "../../services/authService";

const StyledSection = styled.section`
  width: 100vw;
  height: 100vh;

  ${({ theme }: { theme: Theme }) => {
    return css`
      .snackbar {
        margin: ${theme.spacing(1)};
        max-width: 500px;
        text-align: center;
        align-self: center;
        z-index: 2000;
        position: absolute;
      }
      .grid {
        height: 100%;
      }
      .quoteContainer {
        ${theme.breakpoints.down("md")} {
          display: none;
        }
      }
      .quote {
        background: ${theme.palette.background.paper};
        height: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: rgba(25, 75, 180, 0.8);
        background-image: url("/images/dcmBanner.jpg");
        background-blend-mode: overlay;
        background-size: cover;
        background-repeat: no-repeat;
        background-position: center;
      }
      .quoteInner {
        padding: 100px;
        text-align: left;
        flex-basis: 600px;
      }
      .quoteText {
        color: ${theme.palette.common.white};
        font-weight: 300;
        padding-bottom: 30px;
      }
      .name {
        margin-top: ${theme.spacing(3)};
        font-weight: 300;
        color: ${theme.palette.common.white};
      }

      .contentContainer {
      }
      .content {
        height: 100%;
        display: flex;
        flex-direction: column;
        overflow: auto;
      }
      .contentHeader {
        display: flex;
        align-items: center;
        position: absolute;
        padding-top: ${theme.spacing(5)};
        padding-bottom: ${theme.spacing(2)};
        padding-left: ${theme.spacing(2)};
        padding-right: ${theme.spacing(2)};
      }
      .logoImage {
        margin-left: ${theme.spacing(4)};
      }
      .contentBody {
        flex-grow: 1;
        display: flex;
        align-items: center;

        ${theme.breakpoints.down("md")} {
          justify-content: center;
        }
      }
    `;
  }};
`;

const VIEW = {
  signin: "signin",
  signup: "signup",
  signout: "signout",
  confirmCode: "confirmCode",
  resetPassword: "resetPassword", //For simplicity reset is here, move it to profile tab in future
  completeNewPassword: "completeNewPassword",
  forgotPassword: "forgotPassword",
};

export type LoginInput = { email: string; password: string };
export type SignupInput = {
  name: string;
};

export interface AuthContainerProps extends RouterProps {
  setAuth: (auth: boolean) => void;
}

const AuthContainer: React.FC<AuthContainerProps> = props => {
  //This container will have the different views for every auth related action
  //Uses component state for simplicity
  const { history } = props;
  const theme: Theme = useTheme();

  //SHOW MESSAGES
  const [message, setMessage] = useState<string>("");
  const [timer, setTimer] = useState<any>(undefined);
  const showMessage = (message: string) => {
    setMessage(message);
    clearTimeout(timer);
    const timeout = setTimeout(() => {
      setMessage("");
    }, 10000);
    setTimer(timeout);
  };
  const action = (
    <Button
      color="secondary"
      size="small"
      onClick={e => {
        e.preventDefault();
        setMessage("");
      }}
    >
      Close
    </Button>
  );

  //STATE
  const changeView = (view: string) => {
    setView(view);
    localStorage.setItem("AUTH_VIEW", view);
  };
  const loadView = (): string => {
    const prevView = localStorage.getItem("AUTH_VIEW");
    return prevView ? prevView : VIEW.signin;
  };
  const [view, setView] = useState<string>(loadView());

  //SHARED EMAIL
  const changeSharedEmail = (sharedEmail: string) => {
    setSharedEmail(sharedEmail);
    localStorage.setItem("AUTH_SHARED_EMAIL", sharedEmail);
  };
  const loadSharedEmail = () => {
    const prevSharedEmail = localStorage.getItem("AUTH_SHARED_EMAIL");
    return prevSharedEmail ? prevSharedEmail : "";
  };
  const [sharedEmail, setSharedEmail] = useState<string>(loadSharedEmail());

  //NAVIGATION
  const goToHome = () => history.push("/");
  const goToSignup = () => changeView(VIEW.signup);
  const goToSignin = () => changeView(VIEW.signin);
  const goToForgotPassword = () => changeView(VIEW.forgotPassword);

  //FUNCTIONS
  const handleSignup = async (login: LoginInput, input: SignupInput) => {
    const { email, password } = login;
    changeSharedEmail(login.email);
    const { message, nextStage } = await authService.signup(
      email,
      password,
      input.name
    );
    if (message) showMessage(message);
    if (nextStage === STAGE.unconfirmed) changeView(VIEW.confirmCode);
  };

  const handleSignin = async (email: string, password: string) => {
    changeSharedEmail(email);
    const { message, nextStage } = await authService.signin(email, password);
    if (message) showMessage(message);
    if (nextStage === STAGE.signedIn) {
      props.setAuth(true); //Set auth to true before redirect is allowed
      goToHome();
    }
  };

  const handleVerify = async (code: string) => {
    const { message, nextStage } = await authService.confirm(sharedEmail, code);
    if (message) showMessage(message);
    if (nextStage === STAGE.confirmed) changeView(VIEW.signin);
  };

  const handleResend = async () => {
    const { message } = await authService.resendCode(sharedEmail);
    if (message) showMessage(message);
  };
  const handleForgotPassword = async (email: string) => {
    const { message, nextStage } = await authService.forgotPassword(email);
    if (message) showMessage(message);
    if (nextStage === STAGE.forgotPasswordUnconfirmed) {
      showMessage("Code resent");
    }
  };
  const handleForgotPasswordConfirm = async (
    email: string,
    password: string,
    code: string
  ) => {
    changeSharedEmail(email);
    const { message, nextStage } = await authService.forgotPasswordConfirm(
      email,
      code,
      password
    );
    if (message) showMessage(message);
    if (nextStage === STAGE.forgotPasswordConfirmed) changeView(VIEW.signin);
  };

  return (
    <>
      <StyledSection theme={theme}>
        <Grid className="grid" container>
          <Grid className="quoteContainer" item lg={5}>
            <div className="quote">
              <div className="quoteInner">
                <Typography className="quoteText" variant="h1">
                  AI software for informed decision making on crowds
                </Typography>
                <div className="person">
                  <Typography className="name" variant="body1">
                    Increase safety and transform customer experience with
                    automated, real-time, data-driven intelligence
                  </Typography>
                </div>
              </div>
            </div>
          </Grid>
          <Grid className="content" item lg={7} xs={12}>
            {message && (
              <SnackbarContent
                className="snackbar"
                message={message}
                action={action}
              />
            )}
            <div className="content">
              {/* Back button disabled since it doesnt make sense to place it here */}
              {/* <div className="contentHeader">
                <IconButton onClick={handleBack}>
                  <ArrowBackIcon />
                </IconButton>
              </div> */}
              <div className="contentBody">
                {view === VIEW.signin && (
                  <SigninForm
                    sharedEmail={sharedEmail}
                    goToSignup={goToSignup}
                    goToForgotPassword={goToForgotPassword}
                    handleSignin={handleSignin}
                  />
                )}
                {view === VIEW.signup && (
                  <SignupForm
                    goToSignin={goToSignin}
                    handleSignup={handleSignup}
                  />
                )}
                {view === VIEW.confirmCode && (
                  <VerifyForm
                    sharedEmail={sharedEmail}
                    handleVerify={handleVerify}
                    handleResend={handleResend}
                  />
                )}
                {view === VIEW.forgotPassword && (
                  <ForgotPassword
                    sharedEmail={sharedEmail}
                    goToSignup={goToSignup}
                    goToSignin={goToSignin}
                    handleForgotPassword={handleForgotPassword}
                    handleForgotPasswordConfirm={handleForgotPasswordConfirm}
                  />
                )}
                {view === VIEW.signout && <Signout goToSignin={goToSignin} />}
              </div>
            </div>
          </Grid>
        </Grid>
      </StyledSection>
    </>
  );
};

export default AuthContainer;
